import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { endpoints } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Flower2, Clock, Check, ArrowLeft, Heart, Zap, MapPin, HelpCircle, MessageCircleReply, Flag } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const PROFESSIONAL_LABELS = {
  spa: "Spa",
  personal_trainer: "Personal Trainer",
  fitness_movement: "Fitness / Movement",
  massage_bodywork: "Massage / Bodywork",
  nutrition_wellness: "Nutrition / Wellness",
  longevity: "Longevity",
  other: "Wellness Professional",
};

export default function WellnessRitualView() {
  const { code } = useParams();
  const nav = useNavigate();
  const { user } = useAuth() || {};
  const [ritual, setRitual] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [adopting, setAdopting] = useState(false);
  const [adopted, setAdopted] = useState(null); // stores the created ritual once adopted
  const [reacting, setReacting] = useState(false);

  const load = () => {
    endpoints
      .spaByCode(code)
      .then(({ data }) => setRitual(data))
      .catch(() => setNotFound(true));
  };

  useEffect(() => {
    setRitual(null); setNotFound(false); setAdopted(null);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const total = ritual ? ritual.steps.reduce((n, s) => n + (s.minutes || 0), 0) : 0;
  const isAuthor = !!(ritual && user && ritual.author_user_id === user.id);

  const adopt = async () => {
    setAdopting(true);
    try {
      const { data } = await endpoints.spaAdopt(code);
      setAdopted(data);
      toast.success("Added to your rituals.");
    } catch {
      toast.error("Could not add. Try again.");
    }
    setAdopting(false);
  };

  const toggleReact = async (kind) => {
    if (reacting) return;
    setReacting(true);
    try {
      const next = ritual.your_reaction === kind ? null : kind;
      const { data } = await endpoints.spaReact(code, next);
      setRitual((r) => ({ ...r, reaction_counts: data.reaction_counts, your_reaction: data.your_reaction }));
    } catch {
      toast.error("Could not react. Try again.");
    }
    setReacting(false);
  };

  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState("feels_like_a_pitch");
  const [reportNote, setReportNote] = useState("");
  const [reporting, setReporting] = useState(false);
  const [reported, setReported] = useState(false);

  const submitReport = async () => {
    setReporting(true);
    try {
      await endpoints.spaReportRitual(code, reportReason, reportNote.trim() || null);
      setReported(true);
      toast.success("Thanks — a moderator will take a look.");
      setReportOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send the flag.");
    }
    setReporting(false);
  };

  if (notFound) {
    return (
      <div className="max-w-lg mx-auto text-center py-16" data-testid="spa-not-found">
        <div className="font-display text-4xl italic">Not found.</div>
        <p className="mt-4 text-[hsl(var(--muted-foreground))]">
          That ritual code doesn&apos;t exist, or the wellness practice hasn&apos;t published it yet.
        </p>
        <Link to="/rituals" className="inline-block mt-6 text-sm underline underline-offset-4">Back to your rituals</Link>
      </div>
    );
  }

  if (!ritual) {
    return <div className="text-sm text-[hsl(var(--muted-foreground))]" data-testid="spa-view-loading">Opening the door…</div>;
  }

  return (
    <div className="max-w-2xl mx-auto space-y-10" data-testid="spa-ritual-view">
      <button
        onClick={() => nav(-1)}
        data-testid="spa-back"
        className="text-sm text-[hsl(var(--muted-foreground))] inline-flex items-center gap-1 hover:text-[hsl(var(--fg))] tab-link"
      >
        <ArrowLeft size={14} /> Back
      </button>

      <header className="reveal">
        <div className="text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--muted-foreground))] inline-flex items-center gap-2 flex-wrap">
          <Flower2 size={12} className="text-[hsl(var(--terracotta))]" /> {ritual.spa_name}
          <span aria-hidden="true">·</span>
          <span data-testid="spa-view-professional-type">{PROFESSIONAL_LABELS[ritual.professional_type] || "Wellness Professional"}</span>
          {ritual.city && (
            <>
              <span aria-hidden="true">·</span>
              <span className="inline-flex items-center gap-1" data-testid="spa-view-city">
                <MapPin size={11} /> {ritual.city}
              </span>
            </>
          )}
        </div>
        <h1 className="font-display text-5xl lg:text-6xl mt-3 tracking-tight italic leading-[1.05]">
          {ritual.title}
        </h1>
        {ritual.description && (
          <p className="mt-5 text-[hsl(var(--muted-foreground))] leading-relaxed">{ritual.description}</p>
        )}

        {(ritual.intention || ritual.why_useful) && (
          <div className="mt-6 grid md:grid-cols-2 gap-4" data-testid="spa-view-attribution-blocks">
            {ritual.intention && (
              <div className="p-4 border hairline rounded-md bg-[hsl(var(--sand))]/40" data-testid="spa-view-intention">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))]">Intention</div>
                <p className="mt-1.5 text-sm italic leading-relaxed">"{ritual.intention}"</p>
              </div>
            )}
            {ritual.why_useful && (
              <div className="p-4 border hairline rounded-md bg-[hsl(var(--sand))]/40" data-testid="spa-view-why-useful">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))]">Why it works</div>
                <p className="mt-1.5 text-sm italic leading-relaxed">"{ritual.why_useful}"</p>
              </div>
            )}
          </div>
        )}

        <div className="mt-5 flex flex-wrap gap-3 text-sm items-center">
          <span className="px-3 py-1 rounded-full bg-[hsl(var(--sand))] inline-flex items-center gap-1.5">
            <Clock size={12} /> {total} min total
          </span>
          <span className="px-3 py-1 rounded-full bg-[hsl(var(--sand))]">{ritual.time_of_day}</span>
          <span className="px-3 py-1 rounded-full bg-[hsl(var(--sand))]" data-testid="spa-view-code">
            code {ritual.code}
          </span>
          {ritual.reaction_counts && (
            <div className="inline-flex items-center gap-2 ml-auto" data-testid="spa-view-reactions">
              <button
                type="button"
                onClick={() => toggleReact("love")}
                disabled={reacting}
                data-testid="spa-react-love"
                aria-pressed={ritual.your_reaction === "love"}
                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border hairline text-sm tab-link ${
                  ritual.your_reaction === "love" ? "bg-[hsl(var(--terracotta))] text-[hsl(var(--fg))] border-transparent" : "hover:bg-[hsl(var(--sand))]"
                }`}
              >
                <Heart size={13} /> {ritual.reaction_counts.love || 0}
              </button>
              <button
                type="button"
                onClick={() => toggleReact("wow")}
                disabled={reacting}
                data-testid="spa-react-wow"
                aria-pressed={ritual.your_reaction === "wow"}
                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border hairline text-sm tab-link ${
                  ritual.your_reaction === "wow" ? "bg-[hsl(var(--terracotta))] text-[hsl(var(--fg))] border-transparent" : "hover:bg-[hsl(var(--sand))]"
                }`}
              >
                <Zap size={13} /> {ritual.reaction_counts.wow || 0}
              </button>
              {!isAuthor && (
                <button
                  type="button"
                  onClick={() => setReportOpen((v) => !v)}
                  disabled={reported}
                  data-testid="spa-report-toggle"
                  aria-expanded={reportOpen}
                  title={reported ? "Reported" : "This feels like a pitch"}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border hairline text-[11px] tab-link ${
                    reported
                      ? "text-[hsl(var(--muted-foreground))] cursor-default"
                      : "text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--destructive))] hover:border-[hsl(var(--destructive))]/40"
                  }`}
                >
                  <Flag size={11} /> {reported ? "Reported" : "Report"}
                </button>
              )}
            </div>
          )}
        </div>

        {reportOpen && !reported && (
          <div
            className="mt-4 p-4 border hairline rounded-md bg-[hsl(var(--sand))]/30"
            data-testid="spa-report-panel"
          >
            <div className="text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))]">
              This feels like a pitch
            </div>
            <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
              Your report is private. A moderator will review it — the professional never sees who flagged it.
            </p>
            <div className="mt-3 grid sm:grid-cols-2 gap-2">
              {[
                { key: "feels_like_a_pitch", label: "Feels like a pitch" },
                { key: "misleading_claims", label: "Misleading claims" },
                { key: "off_topic", label: "Off-topic" },
                { key: "other", label: "Something else" },
              ].map((r) => (
                <label
                  key={r.key}
                  className={`px-3 py-2 rounded-md border hairline text-sm cursor-pointer tab-link ${
                    reportReason === r.key
                      ? "bg-[hsl(var(--paper))] border-[hsl(var(--terracotta))]/50"
                      : "bg-transparent hover:bg-[hsl(var(--paper))]"
                  }`}
                >
                  <input
                    type="radio"
                    name="report-reason"
                    className="mr-2 accent-[hsl(var(--terracotta))]"
                    checked={reportReason === r.key}
                    onChange={() => setReportReason(r.key)}
                    data-testid={`spa-report-reason-${r.key}`}
                  />
                  {r.label}
                </label>
              ))}
            </div>
            <Textarea
              value={reportNote}
              onChange={(e) => setReportNote(e.target.value)}
              placeholder="Optional context for the moderator…"
              maxLength={280}
              data-testid="spa-report-note"
              className="mt-3 bg-[hsl(var(--paper))] border-[hsl(var(--border))] focus-visible:ring-[hsl(var(--terracotta))] min-h-[60px]"
            />
            <div className="mt-3 flex items-center justify-end gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setReportOpen(false)}
                data-testid="spa-report-cancel"
                className="rounded-full"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={submitReport}
                disabled={reporting}
                data-testid="spa-report-submit"
                className="rounded-full bg-[hsl(var(--terracotta))] hover:bg-[hsl(var(--terracotta))]/90 text-[hsl(var(--fg))]"
              >
                {reporting ? "Sending…" : "Send report"}
              </Button>
            </div>
          </div>
        )}
      </header>

      <ol className="space-y-3 reveal reveal-1" data-testid="spa-view-steps">
        {ritual.steps.map((s, i) => (
          <li key={i} className="p-5 border hairline rounded-md bg-[hsl(var(--paper))]" data-testid={`spa-view-step-${i}`}>
            <div className="flex items-baseline justify-between gap-4">
              <div className="flex items-baseline gap-3 min-w-0">
                <span className="font-display text-2xl text-[hsl(var(--terracotta))] shrink-0">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div className="min-w-0">
                  <div className="font-display text-xl leading-tight">{s.title}</div>
                  {s.product && (
                    <div className="text-sm text-[hsl(var(--muted-foreground))] mt-1 italic">with {s.product}</div>
                  )}
                </div>
              </div>
              <div className="text-sm text-[hsl(var(--muted-foreground))] shrink-0 whitespace-nowrap">{s.minutes} min</div>
            </div>
          </li>
        ))}
      </ol>

      <div className="sticky bottom-4 md:static reveal reveal-2">
        <div className="p-5 border hairline rounded-md bg-[hsl(var(--paper))] shadow-sm flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">Yours to keep</div>
            <p className="font-display text-xl mt-1">
              {adopted ? "Added to your rituals." : "Add these steps to your personal ritual plan."}
            </p>
            <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
              {adopted && adopted.remind_at
                ? `We'll remind you at ${adopted.remind_at} each day. You can change or turn it off anytime.`
                : "Your existing rituals stay untouched."}
            </p>
          </div>
          {adopted ? (
            <Link
              to="/rituals"
              data-testid="spa-view-goto-rituals"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[hsl(var(--moss))] text-[hsl(var(--paper))] hover:bg-[hsl(var(--moss))]/90 tab-link"
            >
              <Check size={14} /> View my rituals
            </Link>
          ) : (
            <Button
              onClick={adopt}
              disabled={adopting}
              data-testid="spa-adopt"
              className="rounded-full bg-[hsl(var(--terracotta))] hover:bg-[hsl(var(--terracotta))]/90 text-[hsl(var(--fg))] px-6 py-6 md:py-3 text-base"
            >
              {adopting ? "Adding…" : "Add to My Rituals"}
            </Button>
          )}
        </div>
      </div>

      <QuestionsSection code={code} isAuthor={isAuthor} />
    </div>
  );
}

function QuestionsSection({ code, isAuthor }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState("");
  const [busy, setBusy] = useState(false);
  const [answerDrafts, setAnswerDrafts] = useState({}); // qid -> text
  const [answering, setAnswering] = useState({}); // qid -> bool

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await endpoints.spaQuestions(code);
      setItems(data || []);
    } catch {
      setItems([]);
    }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [code]);

  const submit = async () => {
    const c = content.trim();
    if (!c) return;
    setBusy(true);
    try {
      await endpoints.spaAskQuestion(code, c);
      setContent("");
      toast.success("Your question was sent to the professional.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send question.");
    }
    setBusy(false);
  };

  const submitAnswer = async (qid) => {
    const c = (answerDrafts[qid] || "").trim();
    if (!c) return;
    setAnswering((a) => ({ ...a, [qid]: true }));
    try {
      await endpoints.spaAnswerQuestion(code, qid, c);
      setAnswerDrafts((d) => ({ ...d, [qid]: "" }));
      toast.success("Answer posted.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not post answer.");
    }
    setAnswering((a) => ({ ...a, [qid]: false }));
  };

  return (
    <section className="reveal reveal-3" data-testid="spa-view-qa">
      <div className="text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))] inline-flex items-center gap-2">
        <HelpCircle size={12} /> Questions · {items.length}
      </div>
      <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))] max-w-2xl">
        Ask the professional about this ritual — no comments, no chatter, just questions and educational answers.
      </p>

      {!isAuthor && (
        <div className="mt-4 p-4 border hairline rounded-md bg-[hsl(var(--paper))]" data-testid="spa-question-form">
          <Textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="What's the best time of day to try this?"
            data-testid="spa-question-input"
            maxLength={500}
            className="bg-transparent border-[hsl(var(--border))] focus-visible:ring-[hsl(var(--terracotta))] min-h-[70px]"
          />
          <div className="mt-3 flex items-center justify-between gap-3">
            <span className="text-[11px] text-[hsl(var(--muted-foreground))]">{content.length}/500</span>
            <Button
              size="sm"
              onClick={submit}
              disabled={busy || content.trim().length < 3}
              data-testid="spa-question-submit"
              className="rounded-full bg-[hsl(var(--terracotta))] hover:bg-[hsl(var(--terracotta))]/90 text-[hsl(var(--fg))]"
            >
              {busy ? "Sending…" : "Ask"}
            </Button>
          </div>
        </div>
      )}

      <ul className="mt-6 space-y-4" data-testid="spa-question-list">
        {loading ? (
          <li className="text-sm text-[hsl(var(--muted-foreground))]" data-testid="spa-question-loading">Loading…</li>
        ) : items.length === 0 ? (
          <li className="text-sm text-[hsl(var(--muted-foreground))] italic" data-testid="spa-question-empty">
            No questions yet. When someone asks, it will land here.
          </li>
        ) : (
          items.map((q) => (
            <li key={q.id} className="p-5 border hairline rounded-md bg-[hsl(var(--paper))]" data-testid={`spa-question-${q.id}`}>
              <div className="text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))]">
                {q.asker_name || "A member"} asked
              </div>
              <p className="mt-2 leading-relaxed">{q.content}</p>
              {q.answer ? (
                <div className="mt-4 pl-4 border-l-2 border-[hsl(var(--moss))]/40" data-testid={`spa-answer-${q.id}`}>
                  <div className="text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--moss))] inline-flex items-center gap-1.5">
                    <MessageCircleReply size={12} /> Answered
                  </div>
                  <p className="mt-1.5 text-sm leading-relaxed">{q.answer}</p>
                </div>
              ) : isAuthor ? (
                <div className="mt-4 pl-4 border-l-2 border-[hsl(var(--sand))]" data-testid={`spa-answer-form-${q.id}`}>
                  <Input
                    value={answerDrafts[q.id] || ""}
                    onChange={(e) => setAnswerDrafts((d) => ({ ...d, [q.id]: e.target.value }))}
                    placeholder="A short, educational answer…"
                    data-testid={`spa-answer-input-${q.id}`}
                    maxLength={1200}
                    className="bg-transparent"
                  />
                  <div className="mt-2 flex justify-end">
                    <Button
                      size="sm"
                      onClick={() => submitAnswer(q.id)}
                      disabled={answering[q.id] || (answerDrafts[q.id] || "").trim().length < 3}
                      data-testid={`spa-answer-submit-${q.id}`}
                      className="rounded-full bg-[hsl(var(--moss))] hover:bg-[hsl(var(--moss))]/90 text-[hsl(var(--paper))]"
                    >
                      {answering[q.id] ? "Posting…" : "Post answer"}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="mt-3 text-[11px] italic text-[hsl(var(--muted-foreground))]" data-testid={`spa-answer-pending-${q.id}`}>
                  Awaiting the professional's response.
                </div>
              )}
            </li>
          ))
        )}
      </ul>
    </section>
  );
}
